<script lang="ts">
  import type { Models } from '@smartlinkdev/types';
  import { error } from '@sveltejs/kit';
  import mapboxgl from 'mapbox-gl';
  import { get } from 'radash';
  import { onMount } from 'svelte';
  import { beforeNavigate, goto } from '$app/navigation';
  import LocationDetailsTooltip from '$lib/components/LocationDetailsTooltip.svelte';
  import MapboxLocationDetailsTooltip from '$lib/components/MapboxLocationDetailsTooltip.svelte';
  import {
    forceRoveMap,
    mapReady,
    locations,
    mapInstance,
    hideControls,
    venue,
    currentLocation,
    currentMapboxMarker,
    isMobile,
  } from '$lib/stores';
  import { activeLanguage } from '$lib/stores/languages';
  import type { PageData } from './$types';

  export let data: PageData;
  let location: Models.Location | undefined;
  let ready = false;

  beforeNavigate(() => {
    if (useRoveMaps()) {
      if ($mapInstance) {
        $mapInstance.removeLocationHighlight(location.id);
        $mapInstance.removeHtmlPin(`htmlPin-${location.id}`);
      }
    } else {
      clearMapboxLocationMarker();
    }
    detailsShown = false;
  });

  onMount(() => {
    if ($mapReady) {
      showLocationDetails();
    }
    ready = true;
  });

  mapReady.subscribe((val) => {
    if (val && ready) {
      showLocationDetails();
    }
  });

  $: {
    location = $locations.find((loc) => loc.id === data.locationId);
    $currentLocation = location;
    if ($mapReady && ready) {
      showLocationDetails();
    }
  }

  function useRoveMaps(): boolean {
    return $forceRoveMap || $venue.settings.mapType === 'rove';
  }

  function clearMapboxLocationMarker(): void {
    if ($currentMapboxMarker) {
      $currentMapboxMarker.remove();
      $currentMapboxMarker = undefined;
    }
  }

  function createMapboxTooltipMarker(location: Models.Location, coords: [number, number]): void {
    clearMapboxLocationMarker();
    if (!window.mapInMemory) {
      console.error('map not ready');
      return;
    }

    const tooltipContainer = document.createElement('div');
    tooltipContainer.style.display = 'block';
    tooltipContainer.style.visibility = 'visible';
    tooltipContainer.style.pointerEvents = 'auto';

    tooltipContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    tooltipContainer.addEventListener('wheel', (e) => {
      e.stopPropagation();
    });

    tooltipContainer.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    });

    tooltipContainer.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });

    const tooltipComponent = new MapboxLocationDetailsTooltip({
      target: tooltipContainer,
      props: { location },
    });


    tooltipComponent.$on('close', () => {
      clearMapboxLocationMarker();

      if ($mapInstance && location.id) {
        $currentLocation = undefined;
        $mapInstance.removeHighlights();
      }
      goto(`/venues/${$venue.id}`);
    });

    $currentMapboxMarker = new mapboxgl.Marker({
      element: tooltipContainer,
      anchor: 'bottom',
      offset: [0, 40],
    })
      .setLngLat(coords)
      .addTo(window.mapInMemory);
  }

  let detailsShown = false;

  const showLocationDetails = () => {
    if (detailsShown) return;

    detailsShown = true;
    if (!$mapInstance) {
      return;
    }

    const found = $locations.find((loc) => loc.id === data.locationId);
    if (!found) {
      detailsShown = false;
      throw error(404, 'Not found');
    }
    const buildingHighlightColor = get($venue, 'settings.mapboxSettings.buildingHighlightColor', '#cc0000') as string;
    $mapInstance.highlightLocationById(found.id, buildingHighlightColor);

    const tempEl = document.createElement('div');
    if (useRoveMaps()) {
      const tooltipComponent = new LocationDetailsTooltip({
        target: tempEl,
        props: {
          location: found,
          mobileView: false,
        },
      });
      const tempElMobile = document.createElement('div');
      new LocationDetailsTooltip({
        target: tempEl,
        props: {
          location: found,
          mobileView: true,
        },
      });
      const tooltipHTML = tempEl;
      $mapInstance.flyinToLocationById(found.id);
      const shape = $mapInstance.getShape(found.id);
      if (!$hideControls && shape) {
        const pinId = `htmlPin-${location.id}`;
        $mapInstance.setHtmlPin({
          id: pinId,
          center: shape.center,
          element: tooltipHTML,
          floorIds: shape.shapeConfig.floorIds,
          isYouAreHere: false,
          startScale: 15,
          centered: true,
          height: 10,
        }, true);
      }
    } else {
      const locationData = $mapInstance.getLocationCenter(found.id);
      if (locationData) {
        $mapInstance.flyinToLocationById(found.id);
        if (!$hideControls && !$isMobile) {
          if (locationData?.center?.length !== 2) {
            console.error('location center is not valid');
            return;
          }
          const coord: [number, number] = [locationData.center[0], locationData.center[1]];
          createMapboxTooltipMarker(found, coord);
        }
      } else {
        clearMapboxLocationMarker();
      }
    }
  };
</script>
